import { SolanaBaseCommand } from './BaseCommand';

import { AnchorProvider, Wallet, web3 } from '@coral-xyz/anchor';
import Solana from '@ledgerhq/hw-app-solana';
import TransportNodeHid from '@ledgerhq/hw-transport-node-hid-singleton';
import { Flags } from '@oclif/core';
import { bs58 } from '@switchboard-xyz/common';
import { InstructionUtils } from '@switchboard-xyz/on-demand';

export abstract class SolanaWithSignerBaseCommand extends SolanaBaseCommand {
  static flags = {
    ...SolanaBaseCommand.flags,
    keypair: Flags.string({
      char: 'k',
      required: false,
      description:
        'keypair that will pay for onchain transactions. defaults to new account authority if no alternate authority provided',
    }),
    asProposal: Flags.boolean({
      description: 'print the transaction as a proposal',
      default: false,
    }),
    ledger: Flags.boolean({
      description: 'enable ledger support',
      exactlyOne: ['keypair'],
    }),
    ledgerPath: Flags.string({
      description: 'HID path to the ledger',
      dependsOn: ['ledger'],
    }),

    // ComputeBudgetProgram flags
    priorityFee: Flags.integer({
      description:
        "set `computeUnitPrice` to boost transactions landing chances ('priority fees')",
      required: false,
      default: 10_000,
    }),
    computeUnitLimitMultiple: Flags.integer({
      description:
        'set `computeUnitLimitMultiple` to add some room for on-chain instructions to be executed',
      required: false,
      default: 1.25,
    }),
  };

  private _ledger?: Solana;

  private _ledgerPath: string;

  private _keypair: web3.Keypair;

  private _asProposal: boolean = false;

  private priorityFee: number;

  private computeUnitLimitMultiple: number;

  async init() {
    await super.init();
    const { flags } = await this.parse(
      this.constructor as typeof SolanaWithSignerBaseCommand
    );
    this.priorityFee = flags.priorityFee;
    this.computeUnitLimitMultiple = flags.computeUnitLimitMultiple;

    this._ledgerPath = flags.ledgerPath ?? "44'/501'";

    if (flags.ledger) {
      const devices = await TransportNodeHid.list();
      console.log(devices);

      const transport = await TransportNodeHid.open();
      this.logConfig(
        { ledger: transport.deviceModel?.productName ?? '' },
        false
      );
      this._ledger = new Solana(transport);

      const ledgerConfig = await this._ledger.getAppConfiguration();
      if (
        !('blindSigningEnabled' in ledgerConfig) ||
        !ledgerConfig.blindSigningEnabled
      ) {
        // TODO: Add instructions link to ledger docs to enable this
        throw new Error('Ledger requires bind signing to be enabled');
      }
    } else if (flags.keypair) {
      const keypairPath =
        flags.keypair || this.ctx.getDefaultAccount('solana', this.cluster);
      if (!keypairPath || keypairPath === undefined) {
        throw new Error("Command requires '--keypair' flag to be provided");
      }

      this._keypair = await this.getSigner(keypairPath);
    }

    this._asProposal = flags.asProposal;

    this.logConfig(
      {
        signer: this.keypair.publicKey.toString(),
        asProposal: this._asProposal ? 'true' : 'false',
      },
      false
    );
  }

  get keypair() {
    return this._keypair;
  }

  get payer() {
    return this.keypair.publicKey;
  }

  get provider() {
    return new AnchorProvider(this.connection, new Wallet(this.keypair), {
      commitment: this.connection.commitment,
    });
  }

  /**
   * Sign and send a transaction.
   *
   * @param ixns Instructions to run as a part of this transaction.
   * @param luts Lookup tables to include in the transaction.
   * @param extraSigners Signers that need to sign the transaction other than the payer keypair.
   */
  async signAndSend(
    ixns: web3.TransactionInstruction[],
    luts?: web3.AddressLookupTableAccount[],
    extraSigners?: web3.Signer[]
  ) {
    if (this._ledger) {
      throw new Error('Ledger not supported at this time.');
    }

    const latestBlockhash = await this.connection.getLatestBlockhash();

    if (this._asProposal) {
      const msg = new web3.TransactionMessage({
        recentBlockhash: latestBlockhash.blockhash,
        payerKey: this.keypair.publicKey,
        instructions: ixns,
      }).compileToLegacyMessage();
      console.log('✅ - Compiled Transaction Message');
      console.log(`    Proposal txn: ${bs58.encode([...msg.serialize()])}`);
    } else {
      const tx = await InstructionUtils.asV0TxWithComputeIxs({
        connection: this.connection,
        ixs: ixns,
        payer: this.keypair.publicKey,
        computeUnitPrice: this.priorityFee,
        computeUnitLimitMultiple: this.computeUnitLimitMultiple,
        lookupTables: luts,
        signers: extraSigners,
      });
      console.log('✅ - Compiled Transaction Message');
      console.log(`    Signing with account: ${this.payer.toBase58()}`);
      console.log(`    Connection rpc url: ${this.connection.rpcEndpoint}`);

      tx.sign([this.keypair]);
      console.log('✅ - Transaction signed');
      if (this.verbose) {
        console.log('    The transaction below is now being sent on-chain.');
        console.log(`    Transaction: ${JSON.stringify(tx, undefined, 2)}`);
      }

      const signature = await this.connection.sendTransaction(tx);
      await this.connection.confirmTransaction({
        signature,
        ...latestBlockhash,
      });
      console.log('✅ - Transaction sent to network');
      console.log(`    Explorer: ${this.toTxUrl(signature)}`);
      console.log(`    Signature: ${signature}`);
    }
  }
}
